import React, { useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Divider,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LensBlurIcon from "@mui/icons-material/LensBlur";
import HistoryIcon from "@mui/icons-material/History";
import UndoIcon from "@mui/icons-material/Undo";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ImageRedactorButton from "./ImageRedactorButton";

interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface ImageRedactorProps {
  src: string;
  onSave: (dataUrl: string) => void;
  documentType: string;
}

const ImageRedactor: React.FC<ImageRedactorProps> = ({
  src,
  onSave,
  documentType,
}) => {
  const { t } = useTranslation();
  const [rects, setRects] = useState<Rect[]>([]);
  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(
    null
  );
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawEnabled, setDrawEnabled] = useState(false);
  const [showWatermark, setShowWatermark] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const today = new Date();
  const watermarkText = `GOHOME`;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const getRelativeCoords = (clientX: number, clientY: number) => {
    const bounds = containerRef.current?.getBoundingClientRect();
    if (!bounds) return { x: 0, y: 0 };
    return {
      x: clientX - bounds.left,
      y: clientY - bounds.top,
    };
  };

  const startDrawing = (x: number, y: number) => {
    setStartPoint({ x, y });
    setRects((prev) => [...prev, { x, y, width: 0, height: 0 }]);
    setIsDrawing(true);
  };

  const updateDrawing = (x: number, y: number) => {
    if (!startPoint) return;
    const { x: sx, y: sy } = startPoint;
    const newX = Math.min(sx, x);
    const newY = Math.min(sy, y);
    const newW = Math.abs(x - sx);
    const newH = Math.abs(y - sy);
    setRects((prev) => {
      const updated = [...prev];
      updated[updated.length - 1] = {
        x: newX,
        y: newY,
        width: newW,
        height: newH,
      };
      return updated;
    });
  };

  const handlePointerDown = (e: React.MouseEvent | React.TouchEvent) => {
    if (drawEnabled) {
      if (!drawEnabled) return;
      const isTouch = "touches" in e;
      const point = isTouch ? e.touches[0] : (e as React.MouseEvent);
      const { x, y } = getRelativeCoords(point.clientX, point.clientY);
      startDrawing(x, y);
    }
  };

  const handlePointerMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (drawEnabled) {
      if (!isDrawing || !startPoint) return;
      const isTouch = "touches" in e;
      const point = isTouch ? e.touches[0] : (e as React.MouseEvent);
      const { x, y } = getRelativeCoords(point.clientX, point.clientY);
      updateDrawing(x, y);
    }
  };

  const handlePointerUp = () => {
    if (drawEnabled) {
      setIsDrawing(false);
      setStartPoint(null);
      setRects((prev) => {
        const last = prev[prev.length - 1];
        if (last.width === 0 || last.height === 0) {
          return prev.slice(0, -1);
        }
        return prev;
      });
    }
  };

  const toggleDraw = () => {
    setDrawEnabled((prev) => !prev);
  };

  const toggleWatermark = () => {
    setShowWatermark((prev) => !prev);
  };

  const clearRects = () => {
    setRects([]);
    setStartPoint(null);
  };

  const undoLastRect = () => {
    setRects((prev) => prev.slice(0, -1));
  };

  const saveImage = useCallback(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      ctx.drawImage(img, 0, 0);

      const scaleX =
        img.naturalWidth /
        (containerRef.current?.clientWidth || img.naturalWidth);
      const scaleY =
        img.naturalHeight /
        (containerRef.current?.clientHeight || img.naturalHeight);
      ctx.fillStyle = "rgba(0,0,0,1)";
      rects.forEach((r) =>
        ctx.fillRect(
          r.x * scaleX,
          r.y * scaleY,
          r.width * scaleX,
          r.height * scaleY
        )
      );

      if (showWatermark) {
        ctx.save();
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((-45 * Math.PI) / 180);
        ctx.font = `${Math.floor(
          canvas.width / (isMobile ? 15 : 12)
        )}px sans-serif`;
        ctx.fillStyle = "rgba(255,0,0,0.28)";
        ctx.textAlign = "center";
        ctx.fillText(watermarkText, 0, 0);
        ctx.fillText(today.toLocaleDateString("nl-NL"), 0, 65);
        ctx.restore();
      }

      onSave(canvas.toDataURL("image/png"));
    };
    img.src = src;
  }, [rects, src, onSave, showWatermark, watermarkText, isMobile]);

  const imageExample =
    documentType === "IDENTITY_FRONT"
      ? "https://rebid-dev-bucket.s3.eu-central-1.amazonaws.com/public/assets/ex-redaction.png"
      : "https://rebid-dev-bucket.s3.eu-central-1.amazonaws.com/public/assets/id-back-dutch.png";

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box
          display="flex"
          gap={3}
          alignItems={isMobile ? "stretch" : "flex-start"}
          justifyContent="space-between"
          flexDirection={isMobile ? "column" : "row"}
        >
          <Box>
            <Typography variant="body2" sx={{ fontSize: isMobile ? 12 : 13 }}>
              <Box
                dangerouslySetInnerHTML={{
                  __html: t("imageRedactor.guide.description"),
                }}
                marginBottom={2}
              />
              {t("imageRedactor.guide.recommended")}
              <ul style={{ margin: 0, paddingLeft: "1.2em" }}>
                {documentType === "IDENTITY_BACK" ? (
                  <>
                    <li>{t("imageRedactor.guide.back.bsn")}</li>
                  </>
                ) : (
                  <>
                    <li>{t("imageRedactor.guide.front.bsn")}</li>
                    <li>{t("imageRedactor.guide.front.photo")}</li>
                  </>
                )}
              </ul>
            </Typography>
          </Box>

          <img
            src={imageExample}
            alt="Redaction Example"
            style={{
              width: isMobile ? "100%" : 300,
              height: isMobile ? "auto" : 200,
              objectFit: "contain",
            }}
          />
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Typography
          variant="body1"
          sx={{
            backgroundColor: "#ffe8e8", // Soft red background
            color: "#b00020", // Darker red text for accessibility
            marginTop: 2,
            padding: 2,
            borderRadius: 2,
            fontWeight: 500,
            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
            fontSize: "0.95rem",
            lineHeight: 1.6,
          }}
          dangerouslySetInnerHTML={{
            __html: t("imageRedactor.guide.warning"),
          }}
        />
        <Divider sx={{ my: 2 }} />
      </Grid>

      <Grid item xs={12}>
        <Box
          display={"flex"}
          gap={3}
          flexDirection={isMobile ? "column" : "row"}
        >
          <Box
            display="flex"
            flexDirection={isMobile ? "row" : "column"}
            justifyContent="flex-start"
            alignItems="center"
            gap={1}
          >
            <ImageRedactorButton
              theme={theme}
              label={t("imageRedactor.button.redact")}
              icon={<LensBlurIcon />}
              onClick={toggleDraw}
              active={drawEnabled}
            />
            <ImageRedactorButton
              theme={theme}
              label={t("imageRedactor.button.undo")}
              icon={<UndoIcon />}
              onClick={undoLastRect}
            />
            <ImageRedactorButton
              theme={theme}
              label={t("imageRedactor.button.watermark")}
              icon={showWatermark ? <VisibilityIcon /> : <VisibilityOffIcon />}
              onClick={toggleWatermark}
              active={showWatermark}
            />
            <ImageRedactorButton
              theme={theme}
              label={t("imageRedactor.button.reset")}
              icon={<HistoryIcon />}
              onClick={clearRects}
            />
          </Box>
          <Box>
            <Box
              ref={containerRef}
              sx={{
                position: "relative",
                display: "inline-block",
                cursor: drawEnabled ? "crosshair" : "default",
                userSelect: "none",
                width: "auto",
                overflow: "hidden",
                touchAction: "none",
              }}
              onMouseDown={handlePointerDown}
              onMouseMove={handlePointerMove}
              onMouseUp={handlePointerUp}
              onTouchStart={handlePointerDown}
              onTouchMove={handlePointerMove}
              onTouchEnd={handlePointerUp}
            >
              <img
                src={src}
                alt="To redact"
                style={{
                  display: "block",
                  pointerEvents: "none",
                  maxWidth: "100%",
                  maxHeight: "300px",
                  width: "auto",
                  height: "auto",
                  objectFit: "contain",
                }}
              />

              {rects.map((r, idx) => (
                <Box
                  key={idx}
                  sx={{
                    position: "absolute",
                    left: r.x,
                    top: r.y,
                    width: r.width,
                    height: r.height,
                    bgcolor: "black",
                    opacity: 1,
                    pointerEvents: "none",
                  }}
                />
              ))}

              {showWatermark && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%) rotate(-45deg)",
                    fontSize: isMobile ? "2.2rem" : "3rem",
                    color: "rgba(255,0,0,0.28)",
                    fontWeight: "bold",
                    pointerEvents: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Box>{watermarkText}</Box>
                  <Box>{today.toLocaleDateString("nl-NL")}</Box>
                </Box>
              )}
              <canvas ref={canvasRef} style={{ display: "none" }} />
            </Box>
          </Box>
        </Box>
      </Grid>

      <Grid marginTop={1} item xs={12}>
        <Box display="flex" justifyContent={"flex-end"} gap={1}>
          <Button size="small" variant="contained" onClick={saveImage}>
            {t("imageRedactor.button.save")}
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
};

export default ImageRedactor;
